/**
 * WebRTC helpers for the staff-side voice path.
 *
 * Phase 1B: staff browser RECEIVES an offer SDP from the backend (forwarded
 * from Meta), creates a peer connection, requests microphone access, generates
 * an answer SDP, waits for ICE gathering to complete, and posts the final SDP
 * back to the backend.
 */

const DEFAULT_ICE_SERVERS: RTCIceServer[] = [
  { urls: 'stun:stun.l.google.com:19302' },
];

export function createPeerConnection(): RTCPeerConnection {
  return new RTCPeerConnection({ iceServers: DEFAULT_ICE_SERVERS });
}

/**
 * Resolves once `pc.iceGatheringState === 'complete'`.
 *
 * Trickle ICE isn't on the table for this transport (the backend forwards a
 * single SDP to Meta), so we wait for the full candidate set before sending.
 */
export function waitForIceGatheringComplete(pc: RTCPeerConnection): Promise<void> {
  if (pc.iceGatheringState === 'complete') return Promise.resolve();

  return new Promise<void>((resolve) => {
    const onChange = () => {
      if (pc.iceGatheringState === 'complete') {
        pc.removeEventListener('icegatheringstatechange', onChange);
        resolve();
      }
    };
    pc.addEventListener('icegatheringstatechange', onChange);

    // Safety net — some browsers never reach 'complete'. Fall back after 2s
    // of silence on the candidate stream.
    let lastCandidateAt = Date.now();
    const onCandidate = (e: RTCPeerConnectionIceEvent) => {
      if (e.candidate) lastCandidateAt = Date.now();
      else {
        // Null candidate = end-of-candidates marker.
        pc.removeEventListener('icecandidate', onCandidate);
        pc.removeEventListener('icegatheringstatechange', onChange);
        resolve();
      }
    };
    pc.addEventListener('icecandidate', onCandidate);

    const interval = setInterval(() => {
      if (pc.iceGatheringState === 'complete' || Date.now() - lastCandidateAt > 2000) {
        clearInterval(interval);
        pc.removeEventListener('icegatheringstatechange', onChange);
        pc.removeEventListener('icecandidate', onCandidate);
        resolve();
      }
    }, 250);
  });
}

export interface AnsweredPeer {
  pc: RTCPeerConnection;
  localStream: MediaStream;
  remoteStream: MediaStream | null;
  sdpAnswer: string;
}

/**
 * Full SDP-answer flow.
 *
 * 1. Create RTCPeerConnection
 * 2. setRemoteDescription(offer)
 * 3. getUserMedia({ audio: true }) and add the track
 * 4. createAnswer + setLocalDescription
 * 5. wait for ICE gathering complete
 * 6. return { pc, sdpAnswer, localStream }
 *
 * The caller owns lifecycle: bind `pc.ontrack` BEFORE awaiting (to catch the
 * first remote track), wire the result into the store, and POST `sdpAnswer`
 * to the backend. Cleanup on End: pc.close() + stop all localStream tracks.
 */
export async function answerOffer(
  offerSdp: string,
  onRemoteTrack: (stream: MediaStream) => void
): Promise<AnsweredPeer> {
  const pc = createPeerConnection();

  let remoteStream: MediaStream | null = null;
  pc.ontrack = (event) => {
    const stream = event.streams[0] ?? null;
    if (stream) {
      remoteStream = stream;
      onRemoteTrack(stream);
    }
  };

  await pc.setRemoteDescription({ type: 'offer', sdp: offerSdp });

  const localStream = await navigator.mediaDevices.getUserMedia({ audio: true });
  for (const track of localStream.getTracks()) {
    pc.addTrack(track, localStream);
  }

  const answer = await pc.createAnswer();
  await pc.setLocalDescription(answer);

  await waitForIceGatheringComplete(pc);

  const finalSdp = pc.localDescription?.sdp;
  if (!finalSdp) {
    throw new Error('Failed to generate local SDP answer');
  }

  return { pc, localStream, remoteStream, sdpAnswer: finalSdp };
}

/** Tear down a peer connection and stop all local-mic tracks. */
export function teardownPeer(
  pc: RTCPeerConnection | null,
  localStream: MediaStream | null
): void {
  try {
    pc?.close();
  } catch {
    /* ignore */
  }
  localStream?.getTracks().forEach((t) => t.stop());
}
